import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { getProducts } from 'api/generated/product-controller/product-controller';
import { getReportById } from 'api/generated/report-controller/report-controller';
import { getPowerBiReports } from 'api/generated/power-bi-controller/power-bi-controller';
import type { ReportRequest } from 'api/generated/schemas/reportRequest';

import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { Loader } from 'ui/loader';
import ReportForm, {
  type ReportFormValues,
  type PowerBiReport,
  type ProductChoice,
} from 'containers/admin/reports/report/report-form';
import toast from 'components/partials/toast/toast';
import { Typography } from 'ui/typography';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import { getActionMenuItems } from 'containers/admin/reports/report/report.helpers';

import { useCurrentUser } from 'hooks/api/use-current-user';
import {
  useCreateReport,
  useModifyReport,
  useDeleteReport,
} from 'hooks/api/use-admin-reports';

const toReportRequest = (data: ReportFormValues): ReportRequest => ({
  title: data.title,
  description: data.description,
  powerBiId: data.powerBiId,
  productId: data.productId ? Number(data.productId) : undefined,
});

interface AdminReportProps {
  reportId: string;
}

interface ReportRecord extends ReportFormValues {
  powerBiTitle?: string;
}

const AdminReport = ({ reportId }: AdminReportProps): React.JSX.Element => {
  const user = useCurrentUser();
  const userId = (user?.data?.oktaUserId ?? '') as string;

  const navigate = useNavigate();
  const isNew = reportId === 'new';

  const createReportMutation = useCreateReport();
  const modifyReportMutation = useModifyReport();
  const deleteReportMutation = useDeleteReport();

  const [reportData, setReportData] = useState<ReportRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(!isNew);
  const [powerBiReports, setPowerBiReports] = useState<PowerBiReport[]>([]);
  const [products, setProducts] = useState<ProductChoice[]>([]);

  const { control, handleSubmit, reset, setFocus, clearErrors } =
    useForm<ReportFormValues>({
      defaultValues: {
        title: '',
        description: '',
        powerBiId: '',
        productId: '',
      },
    });

  useEffect(() => {
    setFocus('title');
  }, [disabled]);

  useEffect(() => {
    const fetchPowerBiReports = async (): Promise<void> => {
      if (!userId) return;

      try {
        const data = await getPowerBiReports(userId, { reportExists: false });

        setPowerBiReports(
          (data ?? []).flatMap((report) =>
            report.id
              ? [{ id: report.id, name: report.name } satisfies PowerBiReport]
              : []
          )
        );
      } catch (error) {
        console.error('Error while fetching power bi reports', error);
      }
    };

    fetchPowerBiReports();
  }, [userId]);

  useEffect(() => {
    const fetchProducts = async (): Promise<void> => {
      if (!userId) return;

      try {
        const response = await getProducts(userId, {
          size: 1000,
          sort: ['name,asc'],
        });

        setProducts(
          (response.data ?? []).flatMap((product) =>
            product.id != null
              ? [{ id: product.id, name: product.name } satisfies ProductChoice]
              : []
          )
        );
      } catch (error) {
        console.error('Error while fetching power bi reports', error);
      }
    };

    fetchProducts();
  }, [userId]);

  useEffect(() => {
    if (!isNew) {
      const fetchReport = async (): Promise<void> => {
        setLoading(true);
        try {
          const data = await getReportById(userId, Number(reportId));

          if (!data) {
            return;
          }

          const { title, description, powerBiId, productId, powerBiTitle } =
            data;

          setReportData({
            title: title ?? '',
            description: description ?? '',
            powerBiId: String(powerBiId ?? ''),
            productId: String(productId ?? ''),
            powerBiTitle,
          });
          reset({
            title: title ?? '',
            description: description ?? '',
            powerBiId: String(powerBiId ?? ''),
            productId: String(productId ?? ''),
          });
        } catch (error) {
          console.error('Error while fetching report data', error);
        } finally {
          setLoading(false);
        }
      };

      fetchReport();
    } else {
      setLoading(false);
    }
  }, [reportId, isNew, reset]);

  const onSubmit = async (data: ReportFormValues): Promise<void> => {
    setLoading(true);

    const reportData = toReportRequest(data);

    try {
      if (isNew) {
        await createReportMutation.mutateAsync({
          userId,
          reportData,
        });
      } else {
        await modifyReportMutation.mutateAsync({
          userId,
          reportId,
          reportData,
        });
      }
      toast.success({
        title: `The report was successfully ${isNew ? 'created' : 'updated'}`,
      });
      navigate('/admin/reports');
    } catch (error) {
      toast.error({ title: 'Something went wrong' });
      console.error('Error while saving report', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setLoading(true);
    try {
      await deleteReportMutation.mutateAsync({ userId, reportId });
      toast.success({
        title: `The report was successfully removed`,
      });
      navigate('/admin/reports');
    } catch (error) {
      toast.error({ title: 'Something went wrong' });
      console.error('Error while report deleting', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    if (isNew) {
      navigate('/admin/reports');
    } else {
      setDisabled(true);
      reset(reportData ?? undefined);
    }
  };

  const reportTitle = isNew ? 'New Report' : reportData?.title;

  return (
    <ResponsiveWrapper customClass="my-4">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Reports', href: '/admin/reports' },
          { label: reportTitle ?? '', isActive: true },
        ]}
      />
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}
      >
        <Typography data-testid="admin-report-heading" variant="h1">
          {reportTitle}
        </Typography>

        {!isNew && (
          <ActionMenu
            buttonName="Edit report"
            options={getActionMenuItems({
              handleDelete: () => {
                void handleDelete();
              },
              reportId,
              reportName: reportData?.title,
            })}
            onEditClick={() => setDisabled(false)}
            isActionMenuDisabled={!disabled}
          />
        )}
      </Stack>

      <Loader loading={loading} fullScreen={false} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ReportForm
          control={control}
          disabled={disabled}
          powerBiReports={powerBiReports}
          products={products}
          clearErrors={clearErrors}
          powerBiData={{
            value: reportData?.powerBiId,
            label: reportData?.powerBiTitle,
          }}
        />

        {(!disabled || isNew) && (
          <Stack direction="row" sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="reserve-text"
              onClick={handleCancel}
              data-testid="cancel-report-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-filled"
              data-testid="save-report-button"
            >
              {isNew ? 'Add Report' : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </form>
    </ResponsiveWrapper>
  );
};

export default AdminReport;
